import { AnalyticsEventName, AnalyticsEventPayloadByName } from './eventTypes';

const DISALLOWED_FIELD_NAME_PATTERN = /token|secret|password|authorization|payload|stack|trace|email|phone|address|\bname\b/i;

const EVENT_ALLOWLISTS: Record<AnalyticsEventName, readonly string[]> = {
  page_view: ['page_path', 'page_title', 'timestamp'],
  map_save: ['map_id', 'layer_count', 'is_update', 'duration_ms', 'timestamp'],
  map_save_fail: ['feature_area', 'error_category', 'error_code', 'retryable', 'map_id', 'layer_count', 'duration_ms', 'timestamp'],
  simulation_run_start: ['rides_count', 'fleet_size', 'frodo_enabled', 'timestamp'],
  simulation_run_complete: ['run_id', 'rides_count', 'fleet_size', 'success_ratio', 'duration_ms', 'timestamp'],
  simulation_run_fail: ['feature_area', 'error_category', 'error_code', 'retryable', 'rides_count', 'fleet_size', 'duration_ms', 'timestamp'],
  export_start: ['export_type', 'export_source', 'item_count', 'timestamp'],
  export_complete: ['export_type', 'export_source', 'item_count', 'duration_ms', 'timestamp'],
  export_fail: ['feature_area', 'error_category', 'error_code', 'retryable', 'export_type', 'export_source', 'item_count', 'duration_ms', 'timestamp'],
  operations_co2_rate_updated: ['rate_kg_per_km', 'source', 'timestamp'],
  operations_co2_rate_invalid: ['input_value', 'fallback_value', 'timestamp'],
  operations_view_render_fail: ['feature_area', 'error_category', 'error_code', 'retryable', 'timestamp'],
  basemap_changed: ['new_basemap', 'previous_basemap', 'timestamp'],
  panel_toggled: ['panel_name', 'is_open', 'screen_area', 'timestamp'],
  simulation_run_selected: ['run_id', 'available_runs_count', 'timestamp'],
  simulation_results_tab_changed: ['tab_name', 'previous_tab', 'timestamp'],
  fixed_routes_tab_changed: ['tab_name', 'previous_tab', 'timestamp'],
  network_route_opened: ['feed_id', 'route_id', 'timestamp'],
  network_route_forked: ['feed_id', 'gtfs_route_id', 'fixed_route_id', 'stop_count', 'timestamp'],
  network_search: ['feed_id', 'query_length', 'route_results', 'stop_results', 'timestamp'],
  network_variant_toggled: ['feed_id', 'route_id', 'direction', 'has_shape', 'timestamp'],
  network_trips_viewed: ['feed_id', 'route_id', 'day', 'direction', 'trip_count', 'timestamp'],
  timetable_grid_mode_toggled: ['surface', 'mode', 'feed_id', 'route_id', 'timestamp'],
  bods_gather_used: ['run_id', 'source', 'stop_count', 'timestamp'],
  bods_network_export: ['feed_id', 'scope', 'route_count', 'edited_count', 'new_count', 'stops_without_atco', 'duration_ms', 'timestamp'],
  network_version_saved: ['feed_id', 'edited_count', 'new_count', 'timestamp'],
  network_version_opened: ['feed_id', 'edited_count', 'new_count', 'timestamp'],
  default_network_changed: ['scope', 'feed_id', 'timestamp'],
  simulation_results_reloaded: ['run_id', 'timestamp'],
  back_to_map_clicked: ['from_screen', 'timestamp'],
  vehicle_search: ['query_length', 'result_count', 'timestamp'],
  vehicle_visibility_changed: ['action', 'vehicle_count', 'timestamp'],
  wizard_opened: ['wizard_session_id', 'entrypoint', 'wizard_version', 'timestamp', 'step_id', 'step_index'],
  wizard_step_viewed: ['wizard_session_id', 'step_id', 'step_index', 'timestamp', 'preset', 'service_mode'],
  wizard_navigation_clicked: [
    'wizard_session_id',
    'step_id',
    'step_index',
    'action',
    'timestamp',
    'target_step_id',
    'target_step_index',
  ],
  wizard_step_completed: [
    'wizard_session_id',
    'step_id',
    'step_index',
    'step_duration_ms',
    'timestamp',
    'used_helper',
  ],
  wizard_dismissed: [
    'wizard_session_id',
    'step_id',
    'step_index',
    'elapsed_session_ms',
    'timestamp',
    'dismiss_source',
    'step_duration_ms',
  ],
  wizard_completed: [
    'wizard_session_id',
    'elapsed_session_ms',
    'timestamp',
    'final_step_id',
    'preset',
    'service_mode',
    'step_duration_ms',
  ],
  wizard_field_group_changed: [
    'wizard_session_id',
    'step_id',
    'step_index',
    'field_group',
    'timestamp',
    'change_source',
  ],
  wizard_default_retained: [
    'wizard_session_id',
    'step_id',
    'step_index',
    'field_group',
    'completion_status',
    'timestamp',
  ],
  wizard_helper_start: ['wizard_session_id', 'step_id', 'step_index', 'helper_type', 'timestamp'],
  wizard_helper_complete: [
    'wizard_session_id',
    'step_id',
    'step_index',
    'helper_type',
    'timestamp',
    'became_authoritative',
    'authoritative_ride_layer_set',
    'authoritative_fleet_set',
  ],
  wizard_helper_fail: [
    'wizard_session_id',
    'step_id',
    'step_index',
    'helper_type',
    'feature_area',
    'error_category',
    'timestamp',
    'error_code',
    'retryable',
  ],
  wizard_validation_blocked: [
    'wizard_session_id',
    'step_id',
    'step_index',
    'validation_reason',
    'timestamp',
  ],
  wizard_summary_edit_opened: ['wizard_session_id', 'step_id', 'step_index', 'field', 'timestamp'],
  wizard_summary_edit_saved: ['wizard_session_id', 'step_id', 'step_index', 'field', 'timestamp'],
  wizard_summary_edit_canceled: ['wizard_session_id', 'step_id', 'step_index', 'field', 'timestamp'],
  tool_directory_searched: ['query_length', 'result_count', 'timestamp'],
  tool_directory_filtered: ['category', 'result_count', 'timestamp'],
  tool_share_opened: ['tool_id', 'timestamp'],
  tool_share_link_copied: ['tool_id', 'fallback_used', 'succeeded', 'timestamp'],
  tool_share_opened_in_new_tab: ['tool_id', 'timestamp'],
  tool_launched: ['tool_id', 'source', 'timestamp'],
  map_tool_activated: ['tool_name', 'activation_source', 'is_active', 'timestamp'],
  fleet_optimiser_field_group_changed: ['field_group', 'timestamp'],
  fleet_optimiser_capex_toggled: ['section', 'enabled', 'timestamp'],
  fleet_optimiser_analyze_clicked: ['has_validation_errors', 'timestamp'],
  fleet_optimiser_result_displayed: ['analysis_years', 'has_capex', 'savings_bucket', 'timestamp'],
  postcode_assignment_file_added: ['file_type', 'upload_method', 'timestamp'],
  postcode_assignment_file_cleared: ['file_type', 'timestamp'],
  postcode_assignment_config_changed: ['field_group', 'timestamp'],
  postcode_assignment_started: [
    'postcode_rows',
    'sheet_count',
    'has_hub_file',
    'osm_search_radius_m',
    'use_osrm_walking_for_consolidation',
    'simulation_output_enabled',
    'simulation_direction',
    'simulation_other_endpoint_mode',
    'timestamp',
  ],
  postcode_assignment_completed: [
    'postcode_rows',
    'geocoded_rows',
    'stops_assigned',
    'hubs_assigned',
    'walking_distance_rows',
    'duration_ms',
    'simulation_output_rows',
    'timestamp',
  ],
  postcode_assignment_failed: [
    'feature_area',
    'error_category',
    'error_code',
    'retryable',
    'failed_stage',
    'duration_ms',
    'timestamp',
  ],
  postcode_assignment_aborted: ['timestamp'],
  postcode_assignment_api_batch: [
    'provider',
    'outcome',
    'batch_index',
    'items_in',
    'items_out',
    'duration_ms',
    'error_category',
    'timestamp',
  ],
  postcode_assignment_result_sheet_selected: ['sheet_index', 'row_count', 'timestamp'],
  postcode_assignment_result_filtered: ['query_length', 'matched_rows', 'timestamp'],
  postcode_assignment_workbook_downloaded: [
    'sheet_count',
    'total_rows',
    'includes_walking_distances',
    'output_kind',
    'timestamp',
  ],
  postcode_assignment_trips_pushed_to_map: [
    'trip_count',
    'passenger_count',
    'consolidated_stop_count',
    'vehicle_count',
    'depot_source',
    'timestamp',
  ],
  autopilot_dashboard_refreshed: ['trigger', 'timestamp'],
  autopilot_jira_import_started: ['timestamp'],
  autopilot_jira_import_completed: [
    'imported',
    'updated',
    'skipped',
    'total_processed',
    'duration_ms',
    'timestamp',
  ],
  autopilot_jira_import_failed: [
    'feature_area',
    'error_category',
    'error_code',
    'retryable',
    'duration_ms',
    'timestamp',
  ],
  autopilot_dashboard_fetched: ['items_count', 'queue_mode', 'duration_ms', 'timestamp'],
  autopilot_item_opened: ['item_id', 'item_status', 'wsjf', 'in_queue', 'timestamp'],
  autopilot_item_tab_changed: ['item_id', 'tab_name', 'previous_tab', 'timestamp'],
  autopilot_artifact_viewed: [
    'item_id',
    'artifact_type',
    'source',
    'outcome',
    'bytes_loaded',
    'timestamp',
  ],
  autopilot_external_link_opened: ['item_id', 'link_type', 'timestamp'],
  autopilot_item_status_updated: [
    'item_id',
    'new_status',
    'has_reason',
    'outcome',
    'timestamp',
  ],
  simulation_results_path_mode_changed: ['mode', 'previous_mode', 'timestamp'],
  simulation_results_vehicle_row_selected: ['source', 'timestamp'],
  simulation_results_vehicle_visibility_toggled: ['is_visible', 'timestamp'],
  simulation_results_unallocated_visibility_changed: ['is_visible', 'timestamp'],
  simulation_results_timeline_window_changed: ['window_hours', 'timestamp'],
  simulation_results_stop_clicked: ['object_type', 'timestamp'],
  simulation_results_modal_opened: ['modal', 'timestamp'],
  simulation_results_import_fixed_routes_started: ['run_id', 'timestamp'],
  simulation_results_import_fixed_routes_succeeded: [
    'run_id',
    'route_count',
    'stop_count',
    'warnings_count',
    'seeded_route_count',
    'missing_geometry_route_count',
    'segment_mismatch_count',
    'named_stop_count',
    'duration_ms',
    'timestamp',
  ],
  simulation_results_import_fixed_routes_failed: ['run_id', 'duration_ms', 'error_code', 'timestamp'],
  admin_analytics_view_changed: ['view', 'previous_view', 'timestamp'],
  admin_analytics_filter_applied: ['filter_dimension', 'active_view', 'timestamp'],
  admin_analytics_inspector_opened: ['target_kind', 'source_view', 'timestamp'],
  admin_analytics_chart_drilled: ['source_chart', 'dimension', 'timestamp'],
  admin_analytics_export_clicked: ['format', 'filtered', 'timestamp'],
  admin_analytics_saved_view_applied: ['has_event_name_filter', 'has_category_filter', 'has_customer_filter', 'timestamp'],
  admin_analytics_share_link_copied: ['active_view', 'param_count', 'has_event_name_filter', 'has_category_filter', 'has_customer_filter', 'has_date_range', 'timestamp'],
  admin_analytics_realtime_paused: ['paused', 'reason', 'timestamp'],
  admin_analytics_cleanup_previewed: ['retention_days', 'estimated_events', 'timestamp'],
  admin_analytics_cleanup_executed: ['retention_days', 'events_deleted', 'timestamp'],
  route_stops_add_started: [
    'environment',
    'product_id',
    'network_id',
    'route_id',
    'version_id',
    'stop_count',
    'timestamp',
  ],
  route_stops_add_completed: [
    'environment',
    'product_id',
    'network_id',
    'route_id',
    'version_id',
    'stop_count',
    'success_count',
    'failure_count',
    'timestamp',
  ],
  route_stops_update_completed: [
    'environment',
    'product_id',
    'network_id',
    'route_id',
    'version_id',
    'stop_count',
    'success_count',
    'failure_count',
    'timestamp',
  ],
  route_stops_delete_completed: [
    'environment',
    'product_id',
    'network_id',
    'route_id',
    'version_id',
    'stop_count',
    'success_count',
    'failure_count',
    'timestamp',
  ],
  route_stops_reorder_completed: [
    'environment',
    'product_id',
    'network_id',
    'route_id',
    'version_id',
    'stop_count',
    'success_count',
    'failure_count',
    'timestamp',
  ],
  fixed_route_vehicle_mapping_saved: [
    'environment',
    'network_id',
    'slot_count',
    'mapped_count',
    'unmapped_count',
    'auto_matched_count',
    'has_presets',
    'timestamp',
  ],
  fixed_route_operations_plan_saved: [
    'environment',
    'network_id',
    'route_id',
    'operation_solution_id',
    'vehicle_count',
    'shift_solution_preview_count',
    'has_route_version',
    'timestamp',
  ],
  fixed_route_attach_operations_opened: [
    'environment',
    'network_id',
    'route_count',
    'seeded_auth',
    'timestamp',
  ],
  fixed_route_shifts_deployed: [
    'environment',
    'route_id',
    'shifts_attempted',
    'shifts_created',
    'shifts_failed',
    'shifts_skipped',
    'timestamp',
  ],
  fixed_route_overlap_run_started: ['feed_id', 'run_id', 'candidate_route_id', 'tolerance_meters', 'version', 'timestamp'],
  fixed_route_overlap_run_completed: ['feed_id', 'run_id', 'candidate_route_id', 'result_count', 'top_overlap_pct', 'duration_ms', 'version', 'timestamp'],
  fixed_route_overlap_run_cancelled: ['feed_id', 'run_id', 'candidate_route_id', 'version', 'timestamp'],
  fixed_route_overlap_run_failed: ['feature_area', 'error_category', 'error_code', 'retryable', 'feed_id', 'run_id', 'candidate_route_id', 'version', 'timestamp'],
  fixed_route_overlap_result_focused: ['feed_id', 'run_id', 'candidate_route_id', 'compared_route_id', 'version', 'timestamp'],
  language_changed: ['from', 'to', 'timestamp'],
  spatial_analysis_run_started: ['polygon_id', 'vertex_count', 'has_source_layer', 'timestamp'],
  spatial_analysis_run_completed: ['run_id', 'layer_count', 'drt_candidate_count', 'duration_ms', 'census_source', 'timestamp'],
  spatial_analysis_run_cancelled: ['run_id', 'timestamp'],
  spatial_analysis_run_failed: ['feature_area', 'error_category', 'error_code', 'retryable', 'run_id', 'timestamp'],
  spatial_analysis_report_expanded: ['run_id', 'report_id', 'section_count', 'source', 'timestamp'],
};

type Primitive = string | number | boolean | null;

function sanitizePrimitive(value: unknown): Primitive | undefined {
  if (value === null) {
    return null;
  }

  if (typeof value === 'boolean' || typeof value === 'number') {
    if (Number.isNaN(value)) {
      return undefined;
    }
    return value;
  }

  if (typeof value === 'string') {
    const normalized = value.trim();
    if (!normalized) {
      return undefined;
    }
    return normalized.slice(0, 120);
  }

  return undefined;
}

export function sanitizeEventPayload<TEventName extends AnalyticsEventName>(
  eventName: TEventName,
  payload: AnalyticsEventPayloadByName[TEventName]
): Partial<AnalyticsEventPayloadByName[TEventName]> {
  // Default to an empty allowlist if the event is somehow not registered (e.g. a
  // stale/HMR bundle mid-reload). Emitting an empty payload is always safe; throwing
  // here (`for…of undefined`) would propagate into the caller and break a user action.
  const allowlist = EVENT_ALLOWLISTS[eventName] ?? [];
  const sanitized: Record<string, Primitive> = {};

  for (const key of allowlist) {
    if (DISALLOWED_FIELD_NAME_PATTERN.test(key)) {
      continue;
    }
    const value = sanitizePrimitive((payload as unknown as Record<string, unknown>)[key]);
    if (value !== undefined) {
      sanitized[key] = value;
    }
  }

  return sanitized as Partial<AnalyticsEventPayloadByName[TEventName]>;
}

export function sanitizeLegacyEvent(event: Record<string, unknown>): Record<string, unknown> {
  const sanitized: Record<string, unknown> = {};

  Object.entries(event).forEach(([key, value]) => {
    if (DISALLOWED_FIELD_NAME_PATTERN.test(key)) {
      return;
    }
    if (
      value === null ||
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      sanitized[key] = value;
    }
  });

  return sanitized;
}
